import React, { useMemo } from 'react';
import { Box, Checkbox, Typography, Chip, Tooltip, IconButton } from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import { FilterChips } from './FilterChips';
import type { TargetTemplateInfo, EditedConfigInfo } from './types';
import type { Planner } from './usePlanner';
import { visibleHiddenSplit, targetMatchesFilter } from './state';

type TargetStatus = 'synced' | 'changed' | 'conflict' | 'incomplete';

const STATUS_LABEL: Record<TargetStatus, string> = {
  synced: 'All keys already in sync',
  changed: 'Will receive updates',
  conflict: 'Existing override on target',
  incomplete: 'No applicable keys',
};

export const TargetExplorer: React.FC<{
  targets: TargetTemplateInfo[];
  editedConfigs: EditedConfigInfo[];
  planner: Planner;
}> = ({ targets, editedConfigs, planner }) => {
  const fuels = useMemo(() => {
    const s = new Set<string>();
    for (const t of targets) s.add(t.fuelType.toUpperCase() || 'NONE');
    return Array.from(s).sort();
  }, [targets]);

  const editedByKey = useMemo(() => {
    const m = new Map<string, EditedConfigInfo>();
    for (const c of editedConfigs) m.set(c.configKey, c);
    return m;
  }, [editedConfigs]);

  const grouped = useMemo(() => {
    const filtered = targets.filter(t => targetMatchesFilter(t, planner.filter));
    const map = new Map<string, { channel: string; fuel: string; templates: TargetTemplateInfo[] }>();
    for (const t of filtered) {
      const fuel = t.fuelType.toUpperCase() || 'NONE';
      const key = `${t.channel}::${fuel}`;
      const entry = map.get(key) ?? { channel: t.channel, fuel, templates: [] };
      entry.templates.push(t);
      map.set(key, entry);
    }
    const order = ['Email', 'Web', 'HER', 'SMS'];
    return Array.from(map.entries()).sort(([, a], [, b]) => {
      const ai = order.indexOf(a.channel);
      const bi = order.indexOf(b.channel);
      if (ai !== bi) return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
      return a.fuel.localeCompare(b.fuel);
    });
  }, [targets, planner.filter]);

  const split = visibleHiddenSplit(planner.selection, targets, planner.filter);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        borderRight: 1,
        borderColor: 'divider',
        bgcolor: '#f8f9fa',
      }}
    >
      <Box sx={{ px: 1.5, pt: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 900,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              fontSize: '0.65rem',
              color: 'text.secondary',
            }}
          >
            Targets
          </Typography>
          <Chip
            label={`${split.visible} Selected${split.hidden > 0 ? ` (${split.hidden} hidden)` : ''}`}
            size="small"
            sx={{
              bgcolor: '#e2e8f0',
              color: '#475569',
              height: 20,
              fontSize: '0.6rem',
              fontWeight: 700,
              '& .MuiChip-label': { px: 1 },
            }}
          />
        </Box>
      </Box>

      <FilterChips fuels={fuels} planner={planner} />

      <Box sx={{ minHeight: 0, flex: 1, overflowY: 'auto', pb: 2 }}>
        {grouped.length === 0 && (
          <Typography variant="body2" sx={{ color: 'text.secondary', px: 2, py: 2 }}>
            No targets match the current filter.
          </Typography>
        )}
        {grouped.map(([groupKey, group]) => {
          const expanded = planner.expandedGroups.has(groupKey);
          const selectedCount = group.templates.filter(t => planner.selection.has(t.templateKey)).length;
          return (
            <Box key={groupKey}>
              <Box
                onClick={() => {
                  const next = new Set(planner.expandedGroups);
                  if (next.has(groupKey)) next.delete(groupKey);
                  else next.add(groupKey);
                  planner.setExpandedGroups(next);
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1,
                  py: 0.5,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'rgba(226,232,240,0.5)' },
                }}
              >
                <IconButton size="small" sx={{ p: 0 }}>
                  {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                </IconButton>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 900,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    fontSize: '0.6rem',
                    color: 'text.disabled',
                    flex: 1,
                  }}
                >
                  {group.channel} · {group.fuel === 'NONE' ? 'DF/CDF' : group.fuel}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>
                  {selectedCount}/{group.templates.length}
                </Typography>
              </Box>
              {expanded &&
                group.templates.map(t => (
                  <TargetRow
                    key={t.templateKey}
                    t={t}
                    planner={planner}
                    status={computeStatus(t, editedByKey)}
                  />
                ))}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

function TargetRow({
  t,
  planner,
  status,
}: {
  t: TargetTemplateInfo;
  planner: Planner;
  status: TargetStatus;
}) {
  const applicable = t.applicableConfigKeys ?? [];
  const sel = planner.selection.get(t.templateKey);
  const checked = !!sel && sel.size > 0;
  const indeterminate = checked && (sel?.size ?? 0) < applicable.length;
  const active = planner.activeTargetKey === t.templateKey;

  return (
    <Tooltip title={STATUS_LABEL[status]} arrow placement="top">
      <Box
        onClick={() => planner.setActiveTargetKey(t.templateKey)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          pl: 2.5,
          pr: 1,
          py: 0.75,
          mx: 0.75,
          mb: 0.25,
          cursor: 'pointer',
          borderRadius: '8px',
          border: active ? '1px solid rgba(0,51,102,0.2)' : '1px solid transparent',
          bgcolor: active ? 'white' : 'transparent',
          boxShadow: active ? '0 1px 3px rgba(0,0,0,0.07)' : 'none',
          '&:hover': { bgcolor: active ? 'white' : 'rgba(226,232,240,0.5)' },
          transition: 'all 0.12s',
        }}
      >
        <Checkbox
          size="small"
          checked={checked}
          indeterminate={indeterminate}
          onChange={() => planner.toggleTarget(t.templateKey, applicable)}
          onClick={e => e.stopPropagation()}
          sx={{ p: 0.25 }}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.8rem',
                fontWeight: active ? 700 : 500,
                color: active ? '#003366' : 'text.primary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {t.displayName}
            </Typography>
            {status === 'conflict' && (
              <Chip
                label="Conflict"
                size="small"
                sx={{
                  bgcolor: '#FEF3C7',
                  color: '#92400E',
                  border: '1px solid #FDE68A',
                  height: 14,
                  fontSize: '0.55rem',
                  fontWeight: 900,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            )}
          </Box>
          <Typography
            variant="caption"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.6rem',
              color: 'text.disabled',
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {t.templateKey}
          </Typography>
        </Box>
        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', flexShrink: 0 }}>
          {sel?.size ?? 0}/{applicable.length}
        </Typography>
      </Box>
    </Tooltip>
  );
}

function computeStatus(
  t: TargetTemplateInfo,
  editedByKey: Map<string, EditedConfigInfo>,
): TargetStatus {
  const applicable = t.applicableConfigKeys ?? [];
  if (applicable.length === 0) return 'incomplete';
  const allSynced = applicable.every(k => {
    const cfg = editedByKey.get(k);
    if (!cfg) return false;
    const copiedEntry = cfg.copiedToTemplates?.find(ct => ct.toTemplateKey === t.templateKey);
    if (!copiedEntry) return false;
    return copiedEntry.isInSync === true && copiedEntry.lastCopiedValue === cfg.newValue;
  });
  if (allSynced) return 'synced';
  const hasOverride = applicable.some(k => {
    const cfg = editedByKey.get(k);
    if (!cfg) return false;
    const copiedEntry = cfg.copiedToTemplates?.find(ct => ct.toTemplateKey === t.templateKey);
    if (copiedEntry?.isInSync === true && copiedEntry.lastCopiedValue === cfg.newValue) return false;
    const targetVal = t.values?.[k];
    return targetVal !== undefined && targetVal !== '' && targetVal !== cfg.newValue;
  });
  return hasOverride ? 'conflict' : 'changed';
}
